// src/data/demons.js — v4 · 12 Oni officiels + admin board data

// ── Incrémente ce numéro à chaque fois que tu remplaces une image Oni ──
// Ça force le navigateur à recharger les images (cache-busting)
// En dev, on veut toujours la dernière version des assets sans gérer le cache.
// Un timestamp au chargement du module suffit à invalider le cache navigateur
// à chaque refresh — pas besoin d'incrémenter ce numéro à la main.
// En prod, repasser à une valeur fixe (ex: 4) pour profiter du cache navigateur normalement.
export const ONI_IMG_VERSION = process.env.NODE_ENV === "production" ? 3 : Date.now();

export const STAT_META = {
  aggression: {
    label: "AGGRESSION",
    color: "#c0392b",
    desc: "Cherche l'attaque à tout prix",
  },
  control: {
    label: "CONTROL",
    color: "#7ecec4",
    desc: "Réduit les variables du jeu",
  },
  chaos: { label: "CHAOS", color: "#b07fe8", desc: "Crée l'imprévisible" },
  adaptation: {
    label: "ADAPTATION",
    color: "#d4a017",
    desc: "Lit et ajuste en temps réel",
  },
  patience: {
    label: "PATIENCE",
    color: "#4a9e6b",
    desc: "Accepte les longs échanges",
  },
  ego: { label: "EGO", color: "#e67e22", desc: "Veut imposer son jeu" },
  creativity: {
    label: "CREATIVITY",
    color: "#5b8dd9",
    desc: "Cherche des solutions originales",
  },
  risk: {
    label: "RISK",
    color: "#e74c3c",
    desc: "Accepte de rater pour gagner gros",
  },
  discipline: {
    label: "DISCIPLINE",
    color: "#8a7e96",
    desc: "Suit son plan quoi qu'il arrive",
  },
  emotion: {
    label: "EMOTION",
    color: "#e91e8c",
    desc: "Fonctionne au momentum",
  },
};

export const ONI_FORMS = [
  {
    id: "crab",
    image: "/oni/crab.jpg",
    emoji: "🦀",
    name: "Crab Oni",
    jp: "蟹鬼",
    sign: "♋",
    signName: "Kani",
    tagline: "Le joueur qui t'enferme.",
    desc: "Latéral. Imprévisible. Il part dans des directions que personne n'anticipe. Ses pinces se referment au moment exact où tu crois avoir l'échange. Patient comme l'eau, violent comme la marée.",
    stats: {
      aggression: 35,
      control: 85,
      chaos: 55,
      adaptation: 70,
      patience: 80,
      ego: 40,
      creativity: 50,
      risk: 30,
      discipline: 75,
      emotion: 45,
    },
    traits: ["fisher", "spinner", "too_many_man"],
    axisPrimary: "control",
    axisSecondary: "patience",
  },
  {
    id: "monkey",
    image: "/oni/monkey.jpg",
    emoji: "🐒",
    name: "Monkey Oni",
    jp: "猿鬼",
    sign: "♊",
    signName: "Saru",
    tagline: "Le joueur génial et imprévisible.",
    desc: "Agile, frénétique, insaisissable. Il fait des trucs qu'aucun coach n'a enseignés. Ça marche une fois sur deux. L'autre fois c'est magnifique quand même.",
    stats: {
      aggression: 60,
      control: 30,
      chaos: 90,
      adaptation: 75,
      patience: 20,
      ego: 55,
      creativity: 95,
      risk: 85,
      discipline: 20,
      emotion: 70,
    },
    traits: ["trick_shot", "old_monkey", "diving_guy"],
    axisPrimary: "chaos",
    axisSecondary: "creativity",
  },
  {
    id: "chick",
    image: "/oni/chick.jpg",
    emoji: "🐥",
    name: "Violent Chick Oni",
    jp: "暴力的な雛鬼",
    sign: "♈",
    signName: "Hina",
    tagline: "Ne sous-estime pas.",
    desc: "La rage concentrée en un corps compact. Frappe plus fort que physiquement possible. Personne ne la voit venir. Tout le monde la voit partir.",
    stats: {
      aggression: 90,
      control: 45,
      chaos: 60,
      adaptation: 50,
      patience: 15,
      ego: 75,
      creativity: 40,
      risk: 80,
      discipline: 35,
      emotion: 95,
    },
    traits: ["rager", "screamer", "slapper"],
    axisPrimary: "aggression",
    axisSecondary: "emotion",
  },
  {
    id: "turtle",
    image: "/oni/turtle.jpg",
    emoji: "🐢",
    name: "Turtle Oni",
    jp: "亀鬼",
    sign: "♑",
    signName: "Kame",
    tagline: "Le survivant.",
    desc: "Il attend. Encore. Et encore. Tu vas faire la faute. Il le sait déjà. Sa carapace absorbe tout. Sa patience est une arme démoniaque que personne ne voit venir.",
    stats: {
      aggression: 15,
      control: 90,
      chaos: 10,
      adaptation: 60,
      patience: 99,
      ego: 25,
      creativity: 30,
      risk: 10,
      discipline: 90,
      emotion: 15,
    },
    traits: ["defender", "fisher", "old_monkey"],
    axisPrimary: "patience",
    axisSecondary: "control",
  },
  {
    id: "frog",
    image: "/oni/frog.jpg",
    emoji: "🐸",
    name: "Frog Oni",
    jp: "蛙鬼",
    sign: "♍",
    signName: "Kaeru",
    tagline: "Le maître des effets.",
    desc: "Ses effets sont une magie noire. La balle fait des choses que la physique interdit. Les gens détestent jouer contre lui. Il le sait. Il sourit. La balle repart dans l'autre sens.",
    stats: {
      aggression: 35,
      control: 70,
      chaos: 80,
      adaptation: 65,
      patience: 60,
      ego: 40,
      creativity: 75,
      risk: 65,
      discipline: 55,
      emotion: 45,
    },
    traits: ["spinner", "illegal_serve", "high_toss"],
    axisPrimary: "chaos",
    axisSecondary: "control",
  },
  {
    id: "seal",
    image: "/oni/seal.jpg",
    emoji: "🦭",
    name: "Seal Oni",
    jp: "アザラシ鬼",
    sign: "♓",
    signName: "Azarashi",
    tagline: "Il applaudit tout. C'est inquiétant.",
    desc: "Il applaudit ses propres points. Il applaudit les points adverses. Il applaudit quand la balle tombe. Il fait des petits bruits. Il sourit toujours. C'est déstabilisant. C'est le but.",
    stats: {
      aggression: 25,
      control: 55,
      chaos: 45,
      adaptation: 75,
      patience: 65,
      ego: 50,
      creativity: 60,
      risk: 35,
      discipline: 45,
      emotion: 80,
    },
    traits: ["excuse_guy", "too_many_man", "lucky_edge"],
    axisPrimary: "adaptation",
    axisSecondary: "emotion",
  },
  {
    id: "spider",
    image: "/oni/spider.jpg",
    emoji: "🕷️",
    name: "Spider Oni",
    jp: "蜘蛛鬼",
    sign: "♏",
    signName: "Kumo",
    tagline: "Le tisseur de pièges.",
    desc: "Méthodique. Il construit sa toile dès le service. Chaque balle placée là exactement pour préparer la suivante. Quand le piège se referme, tu n'as rien vu venir.",
    stats: {
      aggression: 45,
      control: 88,
      chaos: 40,
      adaptation: 80,
      patience: 70,
      ego: 35,
      creativity: 75,
      risk: 35,
      discipline: 85,
      emotion: 30,
    },
    traits: ["illegal_serve", "spinner", "fisher"],
    axisPrimary: "control",
    axisSecondary: "discipline",
  },
  {
    id: "snake",
    image: "/oni/snake.jpg",
    emoji: "🐍",
    name: "Snake Oni",
    jp: "蛇鬼",
    sign: "♐",
    signName: "Hebi",
    tagline: "Le variationniste.",
    desc: "Venimeux et imprévisible. Chaque service est différent. Chaque effet inattendu. Il change de vitesse, de spin, d'angle. Tu crois avoir compris — c'est là qu'il frappe.",
    stats: {
      aggression: 50,
      control: 65,
      chaos: 75,
      adaptation: 85,
      patience: 55,
      ego: 45,
      creativity: 70,
      risk: 60,
      discipline: 50,
      emotion: 55,
    },
    traits: ["spinner", "illegal_serve", "high_toss"],
    axisPrimary: "chaos",
    axisSecondary: "adaptation",
  },
  {
    id: "owl",
    image: "/oni/owl.jpg",
    emoji: "🦉",
    name: "Owl Oni",
    jp: "梟鬼",
    sign: "♎",
    signName: "Fukuro",
    tagline: "Il sait avant que tu joues.",
    desc: "Il te lit. Il analyse chaque tic, chaque regard, chaque micro-mouvement. Il sait exactement où tu vas jouer. Avant toi. Son calme est terrifiant. Sa précision est surnaturelle.",
    stats: {
      aggression: 40,
      control: 80,
      chaos: 25,
      adaptation: 95,
      patience: 75,
      ego: 30,
      creativity: 65,
      risk: 25,
      discipline: 80,
      emotion: 20,
    },
    traits: ["fisher", "defender", "perfect_no_tac"],
    axisPrimary: "adaptation",
    axisSecondary: "control",
  },
  {
    id: "shark",
    image: "/oni/shark.jpg",
    emoji: "🦈",
    name: "Shark Oni",
    jp: "鮫鬼",
    sign: "♈",
    signName: "Same",
    tagline: "Le finisseur.",
    desc: "Il sent le sang dans l'eau. Quand l'adversaire vacille, il accélère. Jamais de pitié. Chaque balle courte est une invitation à l'exécution. Il ne rate pas les balles de match.",
    stats: {
      aggression: 95,
      control: 55,
      chaos: 50,
      adaptation: 60,
      patience: 25,
      ego: 80,
      creativity: 40,
      risk: 70,
      discipline: 60,
      emotion: 65,
    },
    traits: ["attacker", "looper", "screamer"],
    axisPrimary: "aggression",
    axisSecondary: "ego",
  },
  {
    id: "peacock",
    image: "/oni/peacock.jpg",
    emoji: "🦚",
    name: "Peacock Oni",
    jp: "孔雀鬼",
    sign: "♌",
    signName: "Kujaku",
    tagline: "Le showman.",
    desc: "Il joue pour la gallery. Chaque trick shot est une performance. Même quand il perd, c'est beau. L'adversaire est un faire-valoir. La table est une scène. Le public imaginaire applaudit.",
    stats: {
      aggression: 50,
      control: 55,
      chaos: 65,
      adaptation: 55,
      patience: 40,
      ego: 90,
      creativity: 85,
      risk: 75,
      discipline: 35,
      emotion: 80,
    },
    traits: ["trick_shot", "too_many_man", "perfect_no_tac"],
    axisPrimary: "ego",
    axisSecondary: "creativity",
  },
  {
    id: "tanuki",
    image: "/oni/tanuki.jpg",
    emoji: "🦝",
    name: "Tanuki Oni",
    jp: "狸鬼",
    sign: "♓",
    signName: "Tanuki",
    tagline: "Le filou.",
    desc: "Il te fait croire qu'il perd. Il te fait croire qu'il est épuisé. Qu'il maîtrise pas. Puis le score est là et tu comprends trop tard que tu as été manipulé depuis le début.",
    stats: {
      aggression: 40,
      control: 65,
      chaos: 55,
      adaptation: 90,
      patience: 65,
      ego: 60,
      creativity: 80,
      risk: 45,
      discipline: 55,
      emotion: 50,
    },
    traits: ["excuse_guy", "old_monkey", "fisher"],
    axisPrimary: "adaptation",
    axisSecondary: "creativity",
  },
];

export const PERKS_LIST = [
  {
    id: "illegal_serve",
    label: "Service Illégal",
    aggression: 5,
    chaos: 15,
    ego: 10,
    risk: 10,
    discipline: -10,
  },
  {
    id: "cheater",
    label: "Tricheur Assumé",
    chaos: 20,
    ego: 15,
    risk: 15,
    discipline: -15,
    adaptation: 10,
  },
  {
    id: "lucky_edge",
    label: "Bord de Table & Filet Chanceux",
    chaos: 10,
    ego: 5,
    emotion: 10,
  },
  {
    id: "no_backend",
    label: "Revers Inexistant",
    aggression: 10,
    risk: 15,
    discipline: -10,
  },
  { id: "sweater_guy", label: "Survêtement Vintage", ego: 15, patience: 5 },
  {
    id: "tennis_guy",
    label: "Ex-Tennisman",
    chaos: 10,
    ego: 20,
    aggression: 15,
    discipline: -10,
  },
  {
    id: "slapper",
    label: "Frappeur Sec",
    aggression: 20,
    risk: 15,
    discipline: -15,
    emotion: 15,
  },
  {
    id: "excuse_guy",
    label: "Roi des Excuses",
    ego: 20,
    chaos: 10,
    emotion: 15,
    discipline: -10,
  },
  {
    id: "equipment_junky",
    label: "Accro au Matériel",
    chaos: 10,
    creativity: 10,
    risk: 5,
  },
  {
    id: "diving_guy",
    label: "Plongeur",
    chaos: 15,
    risk: 20,
    creativity: 10,
    ego: 10,
  },
  {
    id: "too_many_man",
    label: "Trop de Tics",
    ego: 20,
    patience: 5,
    discipline: 5,
  },
  {
    id: "ball_breaker",
    label: "Casseur de Balles",
    aggression: 20,
    emotion: 25,
    risk: 15,
    discipline: -20,
  },
  { id: "screamer", label: "Crieur", emotion: 25, aggression: 15, ego: 10 },
  {
    id: "rager",
    label: "Soupe au Lait",
    emotion: 30,
    aggression: 20,
    risk: 20,
    discipline: -25,
  },
  { id: "high_toss", label: "Service Lancé Haut", chaos: 15, creativity: 10, ego: 15 },
  {
    id: "perfect_no_tac",
    label: "Technique Parfaite, Tactique Zéro",
    discipline: 20,
    control: 15,
    adaptation: -15,
    ego: 10,
  },
  {
    id: "trick_shot",
    label: "Coup Fantaisie",
    creativity: 25,
    chaos: 20,
    risk: 25,
    discipline: -15,
  },
  {
    id: "old_monkey",
    label: "Vieilles Combines de Vétéran",
    adaptation: 20,
    creativity: 15,
    chaos: 10,
    ego: 15,
  },
  {
    id: "defender",
    label: "Défenseur",
    patience: 25,
    control: 20,
    discipline: 15,
    aggression: -15,
  },
  {
    id: "attacker",
    label: "Attaquant",
    aggression: 20,
    risk: 15,
    ego: 10,
    patience: -10,
  },
  { id: "spinner", label: "Effeur", chaos: 15, creativity: 15, control: 10 },
  {
    id: "looper",
    label: "Lifteur",
    aggression: 15,
    control: 10,
    discipline: 10,
  },
  {
    id: "fisher",
    label: "Pêcheur",
    patience: 20,
    adaptation: 15,
    control: 15,
    aggression: -10,
  },
];

export const QUIZ_STEPS = [
  {
    id: "style",
    question: "Comment tu joues, honnêtement ?",
    subtitle:
      "Pas ce que tu voudrais être. Ce que tu fais vraiment quand c'est 10-10.",
    options: [
      {
        value: "attacker",
        icon: "⚡",
        label: "ATTAQUANT PUR",
        desc: "Je smash. Je loope. Je frappe fort. Parfois dans le filet. Souvent dans le filet. J'assume.",
      },
      {
        value: "defender",
        icon: "🛡️",
        label: "DÉFENSEUR",
        desc: "Je renvoie. Je pousse. J'attends que l'autre craque. Ma patience est une arme.",
      },
      {
        value: "spinner",
        icon: "🌀",
        label: "EFFET & VARIATION",
        desc: "Service vicieux. Effets qui changent. La balle fait des trucs que même moi je comprends pas.",
      },
      {
        value: "placer",
        icon: "🎯",
        label: "PLACEUR TACTIQUE",
        desc: "Je vise les angles. Je construis le point. Je place, je répète, je gagne proprement.",
      },
    ],
    statMods: {
      attacker: {
        aggression: +25,
        ego: +10,
        risk: +20,
        patience: -15,
        discipline: -5,
      },
      defender: {
        patience: +25,
        control: +20,
        discipline: +15,
        aggression: -15,
        risk: -10,
      },
      spinner: { chaos: +20, creativity: +15, control: +10, adaptation: +10 },
      placer: {
        control: +20,
        discipline: +15,
        adaptation: +15,
        creativity: +10,
        aggression: -5,
      },
    },
  },
  {
    id: "mental",
    question: "10-10, jeu décisif. Qu'est-ce qui se passe vraiment en toi ?",
    subtitle: "La vérité sort ici. Pas ce que tu voudrais ressentir.",
    options: [
      {
        value: "rage",
        icon: "🔥",
        label: "LA RAGE PREND LE DESSUS",
        desc: "Je veux détruire la raquette. L'adversaire. La table. Moi-même. Dans cet ordre.",
      },
      {
        value: "clutch",
        icon: "🧊",
        label: "CALME ABSOLU",
        desc: "Je ralentis. Je respire. Je vide la tête. C'est là que je suis le meilleur.",
      },
      {
        value: "ghost",
        icon: "👻",
        label: "JE DISPARAIS",
        desc: "Mon corps joue tout seul. Je regarde de l'extérieur. Dissociation complète.",
      },
      {
        value: "showman",
        icon: "🎭",
        label: "JE PRENDS POSSESSION",
        desc: "C'est mon moment. Le public imaginaire me regarde. Je joue pour ça.",
      },
    ],
    statMods: {
      rage: {
        emotion: +30,
        aggression: +20,
        risk: +15,
        discipline: -20,
        patience: -10,
      },
      clutch: {
        discipline: +25,
        control: +20,
        emotion: -15,
        patience: +15,
        ego: -5,
      },
      ghost: {
        adaptation: +20,
        creativity: +15,
        ego: -10,
        control: +15,
        emotion: +10,
      },
      showman: {
        ego: +30,
        emotion: +15,
        creativity: +10,
        discipline: -10,
        risk: +10,
      },
    },
  },
  {
    id: "adversaire",
    question: "Quel type d'adversaire te dérange le plus ?",
    subtitle: "Celui qui te fait vraiment tilter. Sois honnête.",
    options: [
      {
        value: "puissant",
        icon: "💪",
        label: "L'ATTAQUANT BRUT",
        desc: "Il smash tout. Il réfléchit pas. Il gagne sur la vitesse pure. Je déteste ça.",
      },
      {
        value: "lent",
        icon: "🐢",
        label: "LE DÉFENSEUR INFINI",
        desc: "Il renvoie TOUT. Il attend. Il sourit. Je finis par faire la faute moi-même.",
      },
      {
        value: "bizarre",
        icon: "🤡",
        label: "LE JOUEUR IMPRÉVISIBLE",
        desc: "Picots. Trick shots. Variations folles. Je comprends pas ce qui arrive.",
      },
      {
        value: "mental",
        icon: "🧠",
        label: "LE JOUEUR DANS TA TÊTE",
        desc: "Il te lit. Il anticipe tout. Il sait ce que tu vas jouer avant toi.",
      },
    ],
    statMods: {
      puissant: {
        patience: +20,
        control: +15,
        adaptation: +10,
        aggression: -5,
      },
      lent: { aggression: +20, risk: +15, creativity: +10, patience: -15 },
      bizarre: { adaptation: +25, control: +15, creativity: +10, chaos: -10 },
      mental: {
        chaos: +15,
        creativity: +20,
        ego: +10,
        adaptation: +15,
        discipline: -5,
      },
    },
  },
  {
    id: "matos",
    question: "Ton rapport au matériel, sans mentir.",
    subtitle: "La raquette dit tout sur quelqu'un.",
    options: [
      {
        value: "junky",
        icon: "🛒",
        label: "EQUIPMENT JUNKY",
        desc: "J'ai 4 raquettes minimum. Chaque défaite = nouveau revêtement. Le problème c'est jamais moi.",
      },
      {
        value: "stable",
        icon: "🪨",
        label: "MÊME RAQUETTE",
        desc: "Même setup depuis des années. On souffre ensemble. On gagne ensemble. On ne change pas.",
      },
      {
        value: "picot",
        icon: "👹",
        label: "PICOT ASSUMÉ",
        desc: "Picots longues. Ouais. Je sais ce que tu penses. Je m'en fous. La balle part en sens inverse.",
      },
      {
        value: "beau",
        icon: "✨",
        label: "LA RAQUETTE DOIT ÊTRE BELLE",
        desc: "L'esthétique d'abord. Si c'est pas beau dans la main, je joue pas bien.",
      },
    ],
    statMods: {
      junky: { chaos: +15, creativity: +10, discipline: -15, ego: +10 },
      stable: { discipline: +20, control: +15, patience: +10, creativity: -5 },
      picot: {
        chaos: +25,
        creativity: +15,
        risk: +10,
        adaptation: +15,
        discipline: -10,
      },
      beau: { ego: +25, creativity: +15, adaptation: +5, discipline: -5 },
    },
  },
  {
    id: "behavior",
    question: "Ce que les autres voient de toi en match.",
    subtitle: "Ce que TU penses qu'ils voient. Pas forcément la même chose.",
    options: [
      {
        value: "silent",
        icon: "🗿",
        label: "THE SILENT ONI",
        desc: "Rien. Aucune expression. Les gens savent pas si tu es zen ou sur le point d'exploser. C'est voulu.",
      },
      {
        value: "screamer",
        icon: "📣",
        label: "THE SCREAMER",
        desc: '"Allez !" à chaque point. Même les fautes. Surtout les fautes. Le silence c\'est la mort.',
      },
      {
        value: "ritual",
        icon: "🔮",
        label: "TOO MANY MANIERISMS",
        desc: "Tu rebondis la balle 7 fois avant le service. Tu as un rituel. Chaque. Fois.",
      },
      {
        value: "analyste",
        icon: "🔍",
        label: "L'ANALYSTE",
        desc: "Tu regardes l'adversaire se réchauffer. Tu notes. Tu adaptes. Tu prends ton temps.",
      },
    ],
    statMods: {
      silent: {
        control: +20,
        discipline: +20,
        emotion: -15,
        ego: -5,
        adaptation: +5,
      },
      screamer: {
        emotion: +30,
        aggression: +15,
        ego: +10,
        discipline: -15,
        risk: +5,
      },
      ritual: { ego: +15, patience: +20, discipline: +15, chaos: -5 },
      analyste: {
        adaptation: +25,
        control: +15,
        creativity: +10,
        patience: +10,
        ego: -10,
      },
    },
  },
];

function clamp(v) {
  return Math.max(0, Math.min(99, Math.round(v)));
}

export function computeDemon(answers, selectedPerks) {
  let stats = {
    aggression: 30,
    control: 30,
    chaos: 30,
    adaptation: 30,
    patience: 30,
    ego: 30,
    creativity: 30,
    risk: 30,
    discipline: 30,
    emotion: 30,
  };
  QUIZ_STEPS.forEach((step) => {
    const mods = step.statMods[answers[step.id]] || {};
    Object.entries(mods).forEach(([k, v]) => {
      stats[k] = clamp((stats[k] || 0) + v);
    });
  });
  selectedPerks.forEach((perkId) => {
    const perk = PERKS_LIST.find((p) => p.id === perkId);
    if (!perk) return;
    Object.keys(stats).forEach((k) => {
      if (perk[k]) stats[k] = clamp((stats[k] || 0) + perk[k]);
    });
  });
  let bestOni = ONI_FORMS[0],
    bestScore = -Infinity;
  ONI_FORMS.forEach((oni) => {
    let score = 0;
    Object.keys(stats).forEach((k) => {
      score -= Math.abs((stats[k] || 0) - (oni.stats[k] || 0)) * 0.5;
    });
    const traitMatch = oni.traits.filter((t) =>
      selectedPerks.includes(t),
    ).length;
    score += traitMatch * 35;
    if (stats[oni.axisPrimary] > 60) score += 20;
    if (bestScore < score) {
      bestScore = score;
      bestOni = oni;
    }
  });
  const finalStats = {};
  Object.keys(stats).forEach((k) => {
    finalStats[k] = clamp((stats[k] + (bestOni.stats[k] || 0)) / 2);
  });
  return { oni: bestOni, stats: finalStats, perks: selectedPerks };
}

// ── Horoscope ──────────────────────────────────────────────────────────────
const HOROSCOPE_THEMES = [
  { domain: "SERVICE", label: "Service & Mise en jeu" },
  { domain: "REVERS", label: "Revers & Bloc" },
  { domain: "MENTAL", label: "Mental & Pression" },
  { domain: "DEPLACEMENT", label: "Déplacements" },
  { domain: "ADVERSAIRE", label: "Adversaire de la semaine" },
  { domain: "MATOS", label: "Matériel & Équipement" },
];

const HOROSCOPE_DB = {
  crab: [
    "Ta patience est une griffe. Chaque balle courte est un piège refermé.",
    "Ton contrôle latéral déconcerte les attaquants. Exploite les angles extrêmes.",
    "La pression mentale est ton terrain. Force les échanges longs.",
    "Tes appuis sont des pinces. Reste bas, reste large, rien ne te déplace.",
    "L'attaquant pur est ta proie naturelle. Sa vitesse devient son piège.",
    "Ton matos actuel est parfait. N'y touche pas.",
  ],
  monkey: [
    "Ton service imprévisible va faire deux double-fautes ET deux aces. C'est le deal.",
    "Ton revers bizarre va marcher sur des balles que tu n'aurais pas dû prendre.",
    "Si tu penses trop, tu perds. Joue à l'instinct ou rentre chez toi.",
    "Tes déplacements sont chaotiques mais efficaces. Lean into it.",
    "Le joueur méthodique te craint. Il ne comprend pas ta logique.",
    "Essaie une nouvelle gomme. Ton chaos mérite du carburant frais.",
  ],
  chick: [
    "Frappe la première balle plus fort que d'habitude. Établis la loi dès le service.",
    "Ton revers croisé à pleine vitesse est inarrêtable cette semaine.",
    "Ta rage est ton moteur. Ne la réprime pas — canalise-la.",
    "Avance d'un demi-pas. Tu vas smasher trois balles courtes de suite.",
    "Le Turtle résiste à ta puissance. Varie ou perds.",
    "Une raquette plus rigide amplifierait ta destruction. Enquête.",
  ],
  turtle: [
    "Ton service court et bas est une forteresse. Personne ne passe.",
    "Ton bloc plat renvoie tout. La régularité est ta violence.",
    "À 9-9 tu es le plus calme de la salle. C'est ton superpouvoir démoniaque.",
    "Inutile de te déplacer. Tu es déjà là où la balle arrive.",
    "Le joueur impatient s'auto-détruit contre toi. Donne-lui du temps.",
    "Ta raquette vieillie est une extension de toi. Elle n'a besoin de rien.",
  ],
  frog: [
    "Ton service sidespin-backspin va provoquer deux fautes directes. Compte-les.",
    "Ton revers lifté côté corps adverse est le coup de la semaine.",
    "Souris quand l'adversaire rate ton effet. Ça l'énerve. C'est le but.",
    "Tes changements de rythme surprennent. Reste imprévisible dans le placement.",
    "Le Shark force ta main — il attaque avant que tu poses ta toile. Sers plus court.",
    "Essaie une gomme encore plus capricieuse côté revers. Dans le lore, c'est obligatoire.",
  ],
  seal: [
    "Ton service en double rebond court va être incompréhensible cette semaine.",
    "Après chaque balle gagnée, prends 3 secondes de plus. Observe l'adversaire.",
    "Ton sourire permanent dérange plus que tu ne le crois. Continue.",
    "Tes petits pas chassés sont étrangement efficaces. Personne ne comprend pourquoi.",
    "Le Spider voit tes manœuvres. Contre lui, sois encore plus imprévisible.",
    "Change de raquette juste avant le match. Pour l'effet psychologique.",
  ],
  spider: [
    "Tisse ta toile dès le premier service. Les trois premiers points établissent le pattern.",
    "Ton revers croisé tendu est le fil qui déclenche le piège.",
    "L'adversaire pense avoir compris ton système à 5-5. Il a tort.",
    "Chaque déplacement est calculé. Tu es déjà où tu dois être.",
    "Le Fisher contre-joue tes pièges. Adapte ton système ou change de cible.",
    "Tes picots ou ta gomme lente sont légaux. Pour l'instant.",
  ],
  snake: [
    "Varie le spin à chaque service. Même direction, effet opposé. Observe la panique.",
    "Ton revers lifté suivi d'un amorti — c'est le combo de la semaine.",
    "Change de rythme à 7-7. Accélère d'un coup. L'adversaire est dans ton ancienne vitesse.",
    "Glisse latéralement comme si tu anticipais. Tu anticipes pas. Ça marche quand même.",
    "Le Turtle résiste à tes effets. Ses renvois neutres annulent tout. Attaque plus tôt.",
    "Un changement de revêtement côté revers serait une arme dévastatrice maintenant.",
  ],
  owl: [
    "Tu as déjà lu trois patterns de ton adversaire avant le début du match. Utilise-les.",
    "Ton placement côté revers prévisible de l'adversaire est ton arme cette semaine.",
    "Reste calme à 4-9. Tu as un plan. Il ne l'a pas.",
    "Tes anticipations te font arriver tôt. Cette semaine, attends encore un quart de seconde.",
    "Le Monkey échappe à ta lecture. Son chaos est sincère. Adapte-toi dans le set.",
    "Ton matos sobre sert ton analyse. Ne change rien.",
  ],
  shark: [
    "Attaque la 3e balle sans hésitation. C'est ta semaine pour finir vite.",
    "Ton forehand oblique à pleine vitesse est inarrêtable en ce moment.",
    "Quand l'adversaire tremble, tu accélères encore. Pas de pitié démoniaque.",
    "Avance d'un pas sur le côté coup droit. Tu vas smasher trois balles courtes.",
    "Le Crab te ralentit. Il absorbe ta puissance. Joue plus court que lui.",
    "Une lame plus rigide encore. Ta puissance mérite une meilleure structure.",
  ],
  peacock: [
    "Ton trick shot de la semaine : le coup droit en revers. Entraîne-le en secret.",
    "Le lob suivi d'un smash côté corps — c'est du théâtre pur.",
    "Le public imaginaire te regarde. Joue pour lui. Ça marche sur toi.",
    "Tes pas chassés sont élégants. Ils sont aussi efficaces. Profite.",
    "Le Silent Oni ne réagit pas au show. Ignore-le, joue plus fort.",
    "Ta raquette devrait être belle. Si elle ne l'est pas, corrige ça urgemment.",
  ],
  tanuki: [
    "Fais semblant de rater les deux premiers services. Observe ce que l'adversaire fait.",
    "Ton revers accidentellement court est un art cette semaine.",
    "À 3-7, commence à fatiguer. À 8-7, réveille-toi. L'adversaire est perdu.",
    "Trébuche légèrement une fois. Observe si l'adversaire se détend. Frappe.",
    "Le Spider voit tes pièges — il en pose aussi. Le match le plus intéressant de ta saison.",
    "Garde le même matos pour qu'on sous-estime tes capacités.",
  ],
};

// ── Calcul d'affinité entre deux profils de stats ─────────────────────────
// Retourne un score de "distance" — plus c'est haut, plus les profils sont opposés
function statDistance(statsA, statsB) {
  const keys = Object.keys(statsA);
  let sum = 0;
  keys.forEach(k => {
    const diff = (statsA[k] || 50) - (statsB[k] || 50);
    sum += diff * diff;
  });
  return Math.sqrt(sum / keys.length);
}

// Retourne les Oni les plus "opposés" (à éviter en match) et les plus
// "complémentaires" (bons partenaires d'entraînement) par rapport au profil donné
export function getOniMatchups(myStats, myOniId) {
  if (!myStats) return { avoid: [], train: [] };

  const others = ONI_FORMS.filter(o => o.id !== myOniId);

  const scored = others.map(oni => ({
    oni,
    distance: statDistance(myStats, oni.stats),
  }));

  // Les plus opposés = distance la plus grande -> à éviter en match (style qui te perturbe)
  const avoid = [...scored].sort((a,b) => b.distance - a.distance).slice(0, 3);

  // Les plus complémentaires = distance moyenne (ni trop proche ni trop opposé)
  // -> bon partenaire d'entraînement, différent mais pas déstabilisant à l'extrême
  const sorted = [...scored].sort((a,b) => a.distance - b.distance);
  const midStart = Math.floor(sorted.length * 0.3);
  const train = sorted.slice(midStart, midStart + 3);

  return { avoid, train };
}

// Texte d'explication pour un matchup "à éviter"
export function getAvoidReason(myOniId, theirOni) {
  const reasons = {
    shark:  "Sa vitesse pure ne te laisse aucun temps de préparation.",
    crab:   "Ses angles latéraux cassent ton placement habituel.",
    spider: "Ses pièges et variations rendent ton jeu illisible.",
    owl:    "Il lit ton jeu avant que tu ne le joues toi-même.",
    turtle: "Sa défense infinie te pousse à la faute par frustration.",
    tanuki: "Son jeu trompeur déstabilise ta lecture du point.",
    monkey: "Son chaos pur t'empêche d'installer ton rythme.",
    seal:   "Sa gestion mentale prend l'ascendant psychologique.",
    peacock:"Son ego et sa confiance imposent un tempo difficile.",
    dragon: "Sa puissance brute écrase ton style construit.",
    fox:    "Sa créativité génère des situations que tu n'as jamais entraînées.",
    wolf:   "Sa discipline froide ne laisse passer aucune ouverture.",
  };
  return reasons[theirOni.id] || `Son profil ${STAT_META[theirOni.axisPrimary]?.label?.toLowerCase()} contraste fortement avec le tien.`;
}

// Texte d'explication pour un matchup "entraînement recommandé"
export function getTrainReason(myOniId, theirOni) {
  const reasons = {
    shark:  "Travailler contre sa vitesse améliore tes réflexes sans te détruire.",
    crab:   "Ses variations d'angle t'entraînent à l'adaptation sans te perdre.",
    spider: "Affronter ses pièges aiguise ta lecture sans te paralyser.",
    owl:    "Son analyse te pousse à varier — bon exercice tactique.",
    turtle: "Sa défense t'apprend la patience et la construction de point.",
    tanuki: "Son style trompeur t'entraîne à rester concentré sur l'essentiel.",
    monkey: "Son chaos modéré te désensibilise à l'imprévu.",
    seal:   "Échanger avec lui muscle ta gestion émotionnelle.",
    peacock:"Jouer contre sa confiance t'entraîne à rester sur ton plan.",
    dragon: "Sa puissance te pousse à muscler ta défense sans excès.",
    fox:    "Sa créativité t'expose à des situations enrichissantes.",
    wolf:   "Sa discipline est un bon miroir pour calibrer la tienne.",
  };
  return reasons[theirOni.id] || `Son profil ${STAT_META[theirOni.axisPrimary]?.label?.toLowerCase()} complète intelligemment le tien.`;
}

// ── Tips de coaching par Oni — chaque archétype enseigne sa spécialité ────
export const ONI_TIPS = {
  shark: {
    domain: "VITESSE & FINITION",
    tips: [
      "Réduis ta préparation de bras de moitié. La vitesse vient du timing, pas de l'amplitude.",
      "Sur balle haute, ne réfléchis pas — frappe avant que le doute n'arrive.",
      "Entraîne-toi à smasher depuis une position fermée. Le vrai gain de vitesse est là.",
    ],
  },
  crab: {
    domain: "PLACEMENT & ANGLES",
    tips: [
      "Vise systématiquement les coins extérieurs de la table, pas le centre.",
      "Varie la profondeur avant de varier la direction — ça déstabilise plus.",
      "Un point gagné court vaut autant qu'un point gagné fort. Arrête de tout vouloir finir en force.",
    ],
  },
  spider: {
    domain: "PIÈGES & VARIATION",
    tips: [
      "Sers deux fois la même chose, puis change au moment où l'adversaire s'habitue.",
      "Le meilleur piège n'a l'air de rien — varie la vitesse plus que l'effet.",
      "Observe le placement de pieds adverse avant le service. C'est là que se lit l'anticipation.",
    ],
  },
  owl: {
    domain: "ANALYSE & ANTICIPATION",
    tips: [
      "Pendant l'échauffement, note 3 choses sur le jeu adverse. Utilise-les dès le premier point.",
      "Si tu perds un point, demande-toi pourquoi avant le point suivant — pas après le match.",
      "Repère le coup que l'adversaire évite de jouer. C'est souvent sa faiblesse.",
    ],
  },
  turtle: {
    domain: "DÉFENSE & PATIENCE",
    tips: [
      "Ne cherche pas à gagner le point en défense — cherche juste à ne pas le perdre.",
      "Recule d'un pas de plus que tu ne penses devoir le faire contre un attaquant.",
      "La patience n'est pas passive — observe activement pendant chaque échange long.",
    ],
  },
  tanuki: {
    domain: "DÉCEPTION & RYTHME",
    tips: [
      "Le même geste, deux résultats différents — entraîne ton armement à être illisible.",
      "Ralentis légèrement le rythme avant une accélération — le contraste fait la différence.",
      "Fais croire que tu es fatigué. Frappe fort sur le point suivant.",
    ],
  },
  monkey: {
    domain: "CHAOS CONTRÔLÉ",
    tips: [
      "Le chaos fonctionne mieux avec un minimum de structure — garde un coup fiable en réserve.",
      "Change de tactique seulement après avoir essayé la précédente au moins 3 fois.",
      "Ton imprévisibilité te perturbe parfois toi-même — note ce qui marche pendant le match.",
    ],
  },
  seal: {
    domain: "MENTAL & GESTION ÉMOTIONNELLE",
    tips: [
      "Respire 2 secondes avant chaque service, surtout après une faute.",
      "Sépare le dernier point du suivant — chaque point est sa propre histoire.",
      "Si tu sens la rage monter, ralentis volontairement le rythme du match.",
    ],
  },
  peacock: {
    domain: "CONFIANCE & PRÉSENCE",
    tips: [
      "La confiance se construit par les petits points propres, pas par les coups spectaculaires.",
      "Joue ton meilleur coup tôt dans le match pour t'installer mentalement.",
      "Garde ton langage corporel stable même après une faute — ça déstabilise l'adversaire aussi.",
    ],
  },
  dragon: {
    domain: "PUISSANCE & STRUCTURE",
    tips: [
      "La puissance sans placement est juste de la vitesse pour rien — choisis tes moments.",
      "Travaille ta puissance depuis le revers, c'est souvent le point faible des joueurs puissants.",
      "Une attaque puissante après 2-3 échanges construits est plus efficace qu'en sortie de service.",
    ],
  },
  fox: {
    domain: "CRÉATIVITÉ & ADAPTATION",
    tips: [
      "Essaie un coup non conventionnel à l'entraînement avant de le sortir en match.",
      "Adapte ton jeu après 2 points perdus de la même façon, pas après 5.",
      "La créativité est plus efficace en sortie de service qu'en milieu d'échange.",
    ],
  },
  wolf: {
    domain: "DISCIPLINE & RÉGULARITÉ",
    tips: [
      "Définis un plan de jeu avant le match et ne le change qu'après l'avoir vraiment testé.",
      "La régularité bat la créativité sur un match long — vise la table avant de viser fort.",
      "Compte tes fautes directes pendant le match. Si elles dépassent tes points gagnants, simplifie.",
    ],
  },
};

// Retourne un tip aléatoire (mais stable pour la semaine) pour un Oni donné
export function getOniTip(oniId, weekOffset = 0) {
  const data = ONI_TIPS[oniId];
  if (!data) return null;
  const weekSeed = Math.floor(Date.now() / (7 * 24 * 60 * 60 * 1000)) + weekOffset;
  const tip = data.tips[weekSeed % data.tips.length];
  return { domain: data.domain, tip };
}

export function getWeeklyHoroscope(oniId) {
  const texts = HOROSCOPE_DB[oniId] || HOROSCOPE_DB.monkey;
  const weekSeed = Math.floor(Date.now() / (7 * 24 * 60 * 60 * 1000));
  return HOROSCOPE_THEMES.map((theme, i) => ({
    ...theme,
    text: texts[(weekSeed + i) % texts.length],
    intensity: ["⚡", "🔥", "💀", "⚠️", "✦", "👁️"][(weekSeed + i) % 6],
  }));
}

export const LEVEL_THRESHOLDS = [
  { level: 1, xp: 0, title: "Novice Oni", badge: "🌑" },
  { level: 2, xp: 100, title: "Oni Éveillé", badge: "🌒" },
  { level: 3, xp: 250, title: "Oni Confirmé", badge: "🌓" },
  { level: 4, xp: 500, title: "Oni Affûté", badge: "🌔" },
  { level: 5, xp: 900, title: "Oni Redoutable", badge: "🌕" },
  { level: 6, xp: 1500, title: "Oni Légendaire", badge: "🔥" },
  { level: 7, xp: 2500, title: "Grand Oni", badge: "💀" },
  { level: 8, xp: 4000, title: "Oni Ancestral", badge: "⛩️" },
  { level: 9, xp: 6000, title: "Démon Suprême", badge: "👹" },
  { level: 10, xp: 9999, title: "鬼神", badge: "鬼" },
];

export const BADGES_LIST = [
  {
    id: "first_demon",
    label: "Premier Démon",
    icon: "👹",
    desc: "Créé ton premier profil",
    xp: 50,
  },
  {
    id: "first_coach",
    label: "Première Analyse",
    icon: "🎥",
    desc: "Première session coach",
    xp: 75,
  },
  {
    id: "week_streak",
    label: "Semaine de Feu",
    icon: "🔥",
    desc: "7 jours de journal consécutifs",
    xp: 150,
  },
  {
    id: "ten_sessions",
    label: "Dix Sessions",
    icon: "⚡",
    desc: "10 sessions coach complètes",
    xp: 200,
  },
  {
    id: "oni_master",
    label: "Maître de l'Oni",
    icon: "☯️",
    desc: "Toutes les stats > 60",
    xp: 300,
  },
  {
    id: "full_journal",
    label: "Chroniqueur",
    icon: "📜",
    desc: "30 entrées de journal",
    xp: 250,
  },
];

export function computeLevel(xp) {
  let current = LEVEL_THRESHOLDS[0],
    next = LEVEL_THRESHOLDS[1];
  for (let i = LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
    if (xp >= LEVEL_THRESHOLDS[i].xp) {
      current = LEVEL_THRESHOLDS[i];
      next = LEVEL_THRESHOLDS[i + 1] || null;
      break;
    }
  }
  const progress = next
    ? Math.round(((xp - current.xp) / (next.xp - current.xp)) * 100)
    : 100;
  return { current, next, progress, xp };
}

// ── Admin asset categories ─────────────────────────────────────────────────
export const ADMIN_CATEGORIES = [
  { id: "logo", label: "Logo & Marque", desc: "Logos, wordmarks, signes" },
  { id: "oni", label: "Démons Oni", desc: "Illustrations des 12 Oni" },
  { id: "meme", label: "Memes & Contenu", desc: "Contenu social, memes pong" },
  { id: "sticker", label: "Stickers", desc: "Designs pour stickers" },
  { id: "merch", label: "Merch", desc: "T-shirts, hoodies, posters" },
  {
    id: "pattern",
    label: "Patterns & Textures",
    desc: "Fonds, répétitions, assets",
  },
];
